import { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost/api/v1';

let _cache = null;
let _promise = null;

/**
 * Fetch levels + packs data from the API.
 * Returns { levels: [...], packs: [...] } with packs merged into levels (isPack: true).
 * Cached in memory — subsequent calls return the same promise.
 */
export async function fetchLevelsData() {
    if (_cache) return _cache;
    if (_promise) return _promise;

    _promise = axios.get(`${API_BASE_URL}/levels.php`)
        .then(res => {
            const data = res.data;
            // Merge packs into levels array for backward compat
            const merged = [
                ...data.levels,
                ...(data.packs || [])
            ];
            _cache = { levels: merged, packs: data.packs || [] };
            return _cache;
        })
        .catch(err => {
            console.error('[levelsService] Failed to fetch levels:', err);
            _promise = null; // Allow retry on error
            return null;
        });

    return _promise;
}

/**
 * Return cached data synchronously (null if not fetched yet).
 */
export function getLevelsSync() {
    return _cache;
}

/**
 * Invalidate the cache (call after admin edits).
 */
export function invalidateLevelsCache() {
    _cache = null;
    _promise = null;
}

/**
 * React hook — loads levels data with loading state.
 * @returns {{ levelsData: { levels: Array, packs: Array } | null, loading: boolean }}
 */
export function useLevelsData() {
    const [data, setData] = useState(_cache);
    const [loading, setLoading] = useState(!_cache);

    useEffect(() => {
        if (_cache) {
            setData(_cache);
            setLoading(false);
            return;
        }
        let cancelled = false;
        fetchLevelsData().then(result => {
            if (!cancelled) {
                setData(result);
                setLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, []);

    return { levelsData: data, loading };
}
